/**
 * Popover — a small anchored floating surface, portalled to <body>.
 *
 * The kit has no other overlay-menu primitive, so the schema-driven filter
 * controls (MultiSelect, DateRangePicker, AdvancedFilter) share this one. It is
 * INTERNAL to those components — deliberately not in the barrel, since a bare
 * Popover is a footgun without the menu scaffolding the callers wrap around it.
 *
 * Behaviour (matches the reference desk): opens below its anchor, clamps to the
 * viewport, and self-dismisses on outside-mousedown, Escape, resize, or an
 * OUTSIDE scroll (a scroll inside the panel is ignored so long lists still work).
 * It sits at `--c9-z-popup`, above the modal blanket, so it stays live inside a
 * Modal.
 */
import { Show, createSignal, createEffect, onCleanup, type JSX } from "solid-js";
import { Portal } from "solid-js/web";
import { cn } from "./utils/cn";

export interface PopoverTriggerApi {
  toggle: () => void;
  open: () => boolean;
  setRef: (el: HTMLElement) => void;
}
export interface PopoverPanelApi {
  close: () => void;
}

export function Popover(props: {
  /** Panel width in px (default 240). */
  width?: number;
  /** Align the panel's right edge to the anchor's right edge. */
  align?: "start" | "end";
  trigger: (api: PopoverTriggerApi) => JSX.Element;
  children: (api: PopoverPanelApi) => JSX.Element;
  /** Extra classes for the panel surface. */
  class?: string;
}): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const [pos, setPos] = createSignal({ top: 0, left: 0 });
  let anchor: HTMLElement | undefined;
  let panel: HTMLDivElement | undefined;

  const width = () => props.width ?? 240;

  const place = () => {
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = width();
    let left = props.align === "end" ? r.right - w : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    setPos({ top: r.bottom + 4, left });
  };

  const close = () => setOpen(false);
  const toggle = () => {
    if (!open()) place();
    setOpen((v) => !v);
  };

  createEffect(() => {
    if (!open()) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panel?.contains(t) || anchor?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onScroll = (e: Event) => {
      if (panel && e.target instanceof Node && panel.contains(e.target)) return;
      close();
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    onCleanup(() => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
    });
  });

  return (
    <>
      {props.trigger({ toggle, open, setRef: (el) => (anchor = el) })}
      <Show when={open()}>
        <Portal>
          <div
            ref={panel}
            role="dialog"
            class={cn(
              "fixed flex max-h-[70vh] flex-col overflow-hidden rounded-medium",
              "border border-[color:var(--c9-border)] bg-[var(--c9-surface-overlay)] shadow-[var(--c9-shadow-overlay)]",
              props.class,
            )}
            style={{
              "z-index": "var(--c9-z-popup)",
              top: `${pos().top}px`,
              left: `${pos().left}px`,
              width: `${width()}px`,
            }}
          >
            {props.children({ close })}
          </div>
        </Portal>
      </Show>
    </>
  );
}
